/**
 * GlobalNavigation.js
 *
 * Left sidebar navigation for the entire app. Renders a permanent MUI Drawer
 * (always visible, never collapses) containing a two-tier hierarchy:
 *
 *   Category (collapsible group header)
 *     -> Nav Item (clickable row)
 *
 * Categories displayed — eQMS-first order (quality workflow on top; engineering,
 * ontology, and integration surfaces demoted to supporting inputs). See
 * docs/market-analysis.md "What EIS Is (canonical)" for the rationale.
 *   "Workspace"            -> Projects, Analytics
 *   "Compliance"           -> Compliance (tabs: Standards, Compliance Status, Conformance, Risk Analysis, Design Review Gates, Reports)
 *   "Regulatory Submissions" -> Audit Package, MDR Technical File
 *   "Verification & Validation" -> Verification, V&V Runner (+ DHR tab), IQ / OQ / PQ
 *   "Sources"              -> Requirements, CAD, Documents, Integrations
 *   "Knowledge"            -> Knowledge Graph, Knowledge Base
 *
 * Automation (Orchestrator) is the only true cross-cutting utility item
 * still rendered below the categories.
 *
 * Interaction with App.js:
 *   - Receives `activeSection` (string) and `onSectionChange` (callback) as props.
 *   - Clicking a nav item calls `onSectionChange(sectionId)` which updates
 *     App.js state `activeSection`, causing the main content area to switch
 *     the rendered view component (e.g. ProjectDashboard, UnifiedCADView, etc.).
 *   - The active item is visually highlighted with a left accent bar and
 *     purple text/icon color (#667eea).
 */
import React, { useState } from 'react';
import {
  Drawer,
  List,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Collapse,
  Toolbar,
  Box,
  Divider,
  Chip,
  Tooltip,
  useTheme
} from '@mui/material';
import {
  ExpandLess,
  ExpandMore,
  FolderOpen,
  Assignment,
  Description,
  Workspaces,
  Route,
  VerifiedUser,
  MenuBook,
  Architecture,
  Memory,
  Gavel,
  Layers,
  Insights,
  Hub,
  Polyline,
  ChevronLeft,
  ChevronRight,
  Verified,
  Science,
  Psychology,
  ReportProblemOutlined,
  MonitorHeart,
  Add,
  Remove,
} from '@mui/icons-material';
import { COMPLIANCE_TAB_ORDER } from '../../constants/complianceTabs';

/** Width of the sidebar drawer in pixels (expanded vs icon-only rail). */
const drawerWidth = 260;
const collapsedWidth = 60;

/**
 * @param {string}   activeSection   - Current section ID from App.js state (e.g. 'projects', 'cad', 'parts').
 * @param {Function} onSectionChange - Callback invoked with a section ID string.
 *                                     In App.js this is `handleSectionChange` which calls
 *                                     `setActiveSection(section)` and persists to localStorage.
 */
const GlobalNavigation = ({
  activeSection,
  onSectionChange,
  collapsed = false,
  onToggleCollapsed,
  vocab,
}) => {
  // Dual-mode (2026-07-16): map a nav item's default label through the active
  // profile's vocabulary (e.g. pharma mode renames "V&V Runner" → "Qualification
  // (IQ/OQ/PQ)"). Medtech/default mode has no overrides, so labels pass through.
  const label = (item) => (vocab ? vocab(item.id, item.label) : item.label);
  const theme = useTheme();
  const currentWidth = collapsed ? collapsedWidth : drawerWidth;

  /**
   * openSections -- controls which *category* groups are expanded/collapsed.
   * Keys match category IDs: workspace / compliance / test / sources / knowledge.
   * All default to `true` (expanded) on mount.
   *
   * IA refactor 2026-07-12 (eQMS-first): the old `engineering` cluster is
   * demoted to `sources` (Requirements, CAD, Documents + the Integrations
   * inputs); the new `compliance` cluster (Compliance, Conformance,
   * PL calculator, Audit package) is promoted to the top of the workflow so the
   * QA-director buyer sees quality objects first, not design-eng surfaces.
   * See docs/market-analysis.md "What EIS Is (canonical)".
   */
  const [openSections, setOpenSections] = useState({
    workspace: true,
    compliance: true,
    test: true,
    postmarket: true,
    sources: true,
    knowledge: true,
  });

  /** Toggle a category group open/closed by its ID. */
  const handleToggle = (section) => {
    setOpenSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  /** Track which nav items have their sub-tab lists expanded. */
  const [expandedSubTabs, setExpandedSubTabs] = useState({});

  const toggleSubTabs = (itemId) => {
    setExpandedSubTabs(prev => ({ ...prev, [itemId]: !prev[itemId] }));
  };

  /**
   * navItems -- defines all navigation items in the sidebar.
   *
   * Ordered eQMS-first: prove conformity → verify → (supporting) sources → explore.
   * Quality objects lead; engineering/ontology/integration surfaces are demoted
   * to `sources` so the QA-director buyer isn't greeted by design-eng tools.
   *
   * Each item maps to a section view rendered in App.js's main content area:
   *   'projects'     -> ProjectDashboard / ProjectView
   *   'compliance'   -> ComplianceView         (compliance + DoC + AI Act §12 tabs)
   *   'requirements' -> RequirementsView        (source input)
   *   'cad'          -> UnifiedCADView          (source input)
   *   'documents'    -> DocumentsView           (source input)
   *   'orchestrator' -> OrchestratorView        (pipeline builder)
   *
   * NOTE: item `id`s are unchanged from the pre-2026-07-12 IA — only `category`
   * assignment and ordering changed. App.js routing keys off `id`, so it is
   * untouched by this reorganization.
   *
   * The `category` field groups items under the matching category header.
   * The `badge` field renders a small gradient Chip next to the label.
   */
  const navItems = [
    // Workspace
    { id: 'projects', label: 'Products', icon: <FolderOpen />, category: 'workspace' },
    { id: 'analytics', label: 'Analytics', icon: <Insights />, category: 'workspace' },
    // Compliance — prove conformity (Conformance + Risk Analysis + DHF + Traceability Coverage + Technical File + Audit Package tabs live inside ComplianceView)
    {
      id: 'compliance',
      label: 'Compliance',
      icon: <VerifiedUser />,
      category: 'compliance',
      // Built from the shared COMPLIANCE_TAB_ORDER so the sidebar sub-tabs can
      // never drift from the <Tab> order in ComplianceView. Sub-modes (Conformance:
      // Findings/Coverage, Compliance Status: Current/History, Reports: DoC/Tech
      // File/Audit Package) are reached inside the parent tab.
      subTabs: COMPLIANCE_TAB_ORDER.map((t, i) => ({ label: t.label, tabIndex: i })),
    },
    // Verification & Validation — V-model flow: verify → test → design history
    // IQ/OQ/PQ lives as a tab inside V&V Runner
    {
      id: 'vv',
      label: 'V&V Runner',
      icon: <Verified />,
      category: 'test',
      subTabs: [
        { label: 'Sessions', tabIndex: 0 },
        { label: 'Device History Records', tabIndex: 1 },
        { label: 'IQ / OQ / PQ', tabIndex: 2 },
      ],
    },
    // Post-Market — NCR, CAPA, and surveillance in one place
    {
      id: 'quality-events',
      label: 'Quality Management',
      icon: <ReportProblemOutlined />,
      category: 'postmarket',
      subTabs: [
        { label: 'Events (NCR)', tabIndex: 0 },
        { label: 'CAPA', tabIndex: 1 },
        { label: 'Preventive Actions', tabIndex: 2 },
        { label: 'Post-Market Surveillance', tabIndex: 3 },
      ],
    },
    // Sources — supporting engineering + integration inputs
    {
      id: 'requirements',
      label: 'Requirements',
      icon: <Assignment />,
      category: 'sources',
      subTabs: [
        { label: 'Requirements', tabIndex: 0 },
        { label: 'Import', tabIndex: 1 },
        { label: 'V&V Matrix', tabIndex: 2 },
        { label: 'Risk Register', tabIndex: 3 },
      ],
    },
    { id: 'cad', label: 'CAD', icon: <Architecture />, category: 'sources' },
    { id: 'electronics', label: 'Electronics', icon: <Memory />, category: 'sources' },
    {
      id: 'documents',
      label: 'Documents',
      icon: <Description />,
      category: 'sources',
      subTabs: [
        { label: 'Documents', tabIndex: 0 },
        { label: 'AI Report Generation', tabIndex: 1 },
      ],
    },
    { id: 'integrations', label: 'Integrations', icon: <Hub />, category: 'sources' },
    // Knowledge — ontology / retrieval
    { id: 'knowledge-graph', label: 'Change Impact', icon: <Polyline />, category: 'knowledge' },
    { id: 'knowledge-base', label: 'Knowledge Base', icon: <MenuBook />, category: 'knowledge' },
  ];

  /**
   * categories -- defines the collapsible group headers shown in the sidebar.
   * Each category groups a subset of navItems (matched by `navItem.category === category.id`).
   *
   * IA refactor 2026-07-12 (eQMS-first): `compliance` leads the substantive
   * workflow (prove conformity — the QA-director's job). `test` is the
   * evidence-generation cluster. `sources` (formerly `engineering`) is demoted
   * to supporting inputs and now also absorbs the Integrations
   * connectors. `knowledge` (ontology / retrieval) sits last as a support
   * surface. Category order here drives top-to-bottom render order.
   */
  const categories = [
    { id: 'workspace', label: 'Workspace', icon: <Workspaces sx={{ fontSize: 18 }} /> },
    { id: 'compliance', label: 'Compliance', icon: <Gavel sx={{ fontSize: 18 }} /> },
    { id: 'test', label: 'Verification & Validation', icon: <Science sx={{ fontSize: 18 }} /> },
    { id: 'postmarket', label: 'Post-Market', icon: <MonitorHeart sx={{ fontSize: 18 }} /> },
    { id: 'sources', label: 'Sources', icon: <Layers sx={{ fontSize: 18 }} /> },
    { id: 'knowledge', label: 'Knowledge', icon: <Psychology sx={{ fontSize: 18 }} /> },
  ];

  /**
   * renderNavItem -- renders a single nav item row.
   * Clicking calls onSectionChange(item.id) which sets App.js activeSection.
   */
  const renderNavItem = (item) => {
    const isActive = activeSection === item.id;
    const isDisabled = !!item.disabled;
    const button = (
      <ListItemButton
        key={item.id}
        disabled={isDisabled}
        onClick={isDisabled ? undefined : () => onSectionChange(item.id)}
        sx={{
          borderRadius: 2,
          ...(collapsed && {
            minWidth: 0,
            justifyContent: 'center',
            px: 0,
            py: 1,
            mx: 0.5,
          }),
          mb: 0.5,
          py: 1,
          pl: 2,
          position: 'relative',
          overflow: 'hidden',
          bgcolor: isActive ? 'rgba(102, 126, 234, 0.12)' : 'transparent',
          /* Active indicator: thin gradient bar on left edge */
          '&::before': isActive ? {
            content: '""',
            position: 'absolute',
            left: 0,
            top: '50%',
            transform: 'translateY(-50%)',
            width: 3,
            height: '60%',
            borderRadius: '0 4px 4px 0',
            background: 'linear-gradient(180deg, #667eea 0%, #764ba2 100%)',
          } : {},
          '&:hover': {
            bgcolor: isActive
              ? 'rgba(102, 126, 234, 0.15)'
              : 'rgba(0, 0, 0, 0.04)',
          },
          transition: 'all 0.2s ease',
        }}
      >
        <ListItemIcon
          sx={{
            minWidth: collapsed ? 0 : 36,
            justifyContent: 'center',
            color: isActive ? '#667eea' : 'text.secondary',
            transition: 'color 0.2s ease',
          }}
        >
          {item.icon}
        </ListItemIcon>
        {!collapsed && (
        <ListItemText
          primary={
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {label(item)}
              {/*
               * Badge chip (e.g. "Preview" on Orchestrator).
               * Rendered only when item.badge is defined. Uses the app's
               * signature purple gradient. This visually signals that the
               * feature is in preview/beta state.
               */}
              {item.badge && (
                <Chip
                  label={item.badge}
                  size="small"
                  sx={{
                    height: 18,
                    fontSize: '0.6rem',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    color: '#fff',
                    letterSpacing: '0.3px',
                  }}
                />
              )}
            </Box>
          }
          primaryTypographyProps={{
            fontSize: '0.875rem',
            fontWeight: isActive ? 600 : 400,
            color: isActive ? '#667eea' : 'text.primary',
            component: 'div',
          }}
        />
        )}
        {!collapsed && item.subTabs && (
          <Box
            component="span"
            onClick={(e) => { e.stopPropagation(); toggleSubTabs(item.id); }}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              width: 22,
              height: 22,
              borderRadius: 0.75,
              flexShrink: 0,
              color: expandedSubTabs[item.id] ? '#667eea' : 'text.secondary',
              '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.12)', color: '#667eea' },
              transition: 'all 0.15s ease',
            }}
          >
            {expandedSubTabs[item.id]
              ? <Remove sx={{ fontSize: 14 }} />
              : <Add sx={{ fontSize: 14 }} />
            }
          </Box>
        )}
      </ListItemButton>
    );
    // In collapsed rail, icons need their label as a hover tooltip — otherwise
    // the user has no way to know what each glyph means.
    if (collapsed) {
      const tip = isDisabled && item.disabledTooltip ? item.disabledTooltip : label(item);
      return (
        <Tooltip key={item.id} title={tip} placement="right">
          <span>{button}</span>
        </Tooltip>
      );
    }
    if (isDisabled && item.disabledTooltip) {
      return (
        <Tooltip key={item.id} title={item.disabledTooltip} placement="right">
          <span>{button}</span>
        </Tooltip>
      );
    }
    return button;
  };

  /**
   * drawerContent -- the full sidebar layout:
   *   <Toolbar />           spacer so content starts below the fixed AppBar
   *   <scrollable area>     iterates `categories`, each containing:
   *     - Collapsible category header (click to toggle)
   *     - Filtered navItems rendered via renderNavItem()
   *     - Divider between categories (except after the last one)
   *   <bottom spacing>
   */
  const drawerContent = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Empty Toolbar acts as a spacer to push content below the fixed AppBar */}
      <Toolbar />

      {/* Collapse / expand toggle. In collapsed mode the icons run flat with no
          category headers, so the toggle is the only chrome at the top. */}
      {onToggleCollapsed && (
        <Box sx={{ display: 'flex', justifyContent: collapsed ? 'center' : 'flex-end', px: 1, pt: 1 }}>
          <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
            <ListItemButton
              onClick={onToggleCollapsed}
              sx={{ borderRadius: 1.5, p: 0.5, minWidth: 0, width: 32, height: 32, justifyContent: 'center' }}
            >
              {collapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
            </ListItemButton>
          </Tooltip>
        </Box>
      )}

      <Box sx={{ flex: 1, overflowY: 'auto', px: collapsed ? 0.5 : 1.5, py: 1.5 }}>
        {/* Collapsed mode: flat icon list, no category headers, no dividers
            between groups — the rail is too narrow for them and they add
            visual noise. */}
        {collapsed ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
            {/* The collapsed rail iterates the full navItems set in order
                (category grouping is dropped when collapsed), then appends the
                cross-cutting Automation item below. */}
            {navItems.map(item => renderNavItem(item))}
            <Box sx={{ height: 8 }} />
            {renderNavItem({ id: 'orchestrator', label: 'Workflows', icon: <Route /> })}
          </Box>
        ) : (
        <>
        {/* Render every category as a collapsible group in `categories` order,
            with a divider between each. All nav items now live under a category
            (the old `standalone` mechanism was removed with the 2026-07-12 IA
            reorg). */}
        {(() => {
          const elements = [];

          const renderCategory = (category) => {
            elements.push(
              <Box key={category.id} sx={{ mb: 1 }}>
                <ListItemButton
                  onClick={() => handleToggle(category.id)}
                  sx={{
                    borderRadius: 2,
                    mb: 0.5,
                    py: 1,
                    '&:hover': {
                      bgcolor: 'rgba(102, 126, 234, 0.08)',
                    },
                  }}
                >
                  <ListItemIcon sx={{ minWidth: 32, color: '#667eea' }}>
                    {category.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={category.label}
                    primaryTypographyProps={{
                      fontSize: '0.8rem',
                      fontWeight: 600,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.5px',
                    }}
                  />
                  {openSections[category.id] ? (
                    <ExpandLess sx={{ fontSize: 20, color: 'text.secondary' }} />
                  ) : (
                    <ExpandMore sx={{ fontSize: 20, color: 'text.secondary' }} />
                  )}
                </ListItemButton>
                <Collapse in={openSections[category.id]} timeout="auto" unmountOnExit>
                  <List component="div" disablePadding sx={{ pl: 1 }}>
                    {navItems.filter(i => i.category === category.id).map(item => (
                      <React.Fragment key={item.id}>
                        {renderNavItem(item)}
                        {item.subTabs && !collapsed && expandedSubTabs[item.id] && (
                          <List disablePadding>
                            {item.subTabs.map(subTab => (
                              <ListItemButton
                                key={subTab.tabIndex}
                                onClick={() => onSectionChange(item.id, subTab.tabIndex)}
                                sx={{
                                  borderRadius: 1.5,
                                  py: 0.4,
                                  pl: 4.5,
                                  mb: 0.25,
                                  '&:hover': { bgcolor: 'rgba(102, 126, 234, 0.08)' },
                                }}
                              >
                                <ListItemText
                                  primary={subTab.label}
                                  primaryTypographyProps={{
                                    fontSize: '0.8rem',
                                    color: 'text.secondary',
                                  }}
                                />
                              </ListItemButton>
                            ))}
                          </List>
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                </Collapse>
              </Box>
            );
          };

          categories.forEach((category, i) => {
            if (i > 0) {
              elements.push(
                <Divider key={`div-${category.id}`} sx={{ my: 1.5, mx: 1, borderColor: 'rgba(0, 0, 0, 0.06)' }} />
              );
            }
            renderCategory(category);
          });

          return elements;
        })()}

        {/* Utility items — outside category groups.
            IA refactor 2026-05-29: KG + KB moved to the new `knowledge`
            category above; only Orchestrator remains here as a true
            utility (cross-cutting pipeline builder). */}
        <Divider sx={{ my: 1.5, mx: 1, borderColor: 'rgba(0, 0, 0, 0.06)' }} />
        <Box sx={{ px: 0.5 }}>
          {renderNavItem({ id: 'orchestrator', label: 'Automation', icon: <Route /> })}
        </Box>
        </>
        )}
      </Box>

      {/* Bottom spacing */}
      <Box sx={{ pb: 4 }} />
    </Box>
  );

  return (
    <Drawer
      variant="permanent"
      data-tour="global-nav"
      sx={{
        width: currentWidth,
        flexShrink: 0,
        transition: 'width 0.2s ease',
        [`& .MuiDrawer-paper`]: {
          width: currentWidth,
          boxSizing: 'border-box',
          borderRight: '1px solid',
          borderColor: 'divider',
          overflowX: 'hidden',
          transition: 'width 0.2s ease',
          /* Subtle vertical gradient background, adapts to dark/light mode */
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(180deg, #1e1e1e 0%, #121212 100%)'
            : 'linear-gradient(180deg, #ffffff 0%, #f8fafc 100%)',
        },
      }}
    >
      {drawerContent}
    </Drawer>
  );
};

export default GlobalNavigation;
